import * as XLSX from "xlsx";
import { Platform } from "react-native";
import moment from "moment";
import { orderTrackingHeaders, nestedOrderTrackingHeaders } from "./headers";

let FileSystem: any;
let Sharing: any;

if (Platform.OS !== "web") {
  FileSystem = require("expo-file-system");
  Sharing = require("expo-sharing");
}

const excludeHeaderKeys = [
  "trackerActions_sm",
  "trackerActions",
  "actions",
  "Actions",
];

const getFilteredHeaders = (headers: Record<string, any>) =>
  Object.keys(headers).filter((key) => !excludeHeaderKeys.includes(key));

const formatDateTime = (value: any) => {
  if (!value) return "";
  const date = moment.utc(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : value ?? "";
};

const formatDate = (value: any) => {
  if (!value) return "";
  const date = moment.utc(value);
  return date.isValid() ? date.format("DD/MM/YYYY") : value ?? "";
};

const factoryDateFields = new Set([
  "planStartDay",
  "actualStartDay",
  "ESTCompletionDate",
  "actualCompletionDate",
  "shippingDeadline",
  "ETD",
  "ETA",
  "ExpectedArrival",
]);

const shipmentDateFields = new Set(["ETD", "ETA"]);

export const exportDataToExcelMultiSheet = async (
  filteredOrders: any[],
  fileName: string,
  sheetNames: string[],
  t: (key: string) => string
) => {
  try {
    // Sheet 1: Main Orders
    const mainSheet = filteredOrders.map((o) =>
      getFilteredHeaders(orderTrackingHeaders).reduce((acc, key) => {
        let value = o[key];

        if (key === "allocateProductionLine" && Array.isArray(value)) {
          value = value.join(",");
        }

        acc[orderTrackingHeaders[key].name] = value ?? "";
        return acc;
      }, {} as Record<string, any>)
    );

    // Sheet 2: Factory Details
    const factorySheet = filteredOrders.flatMap((order) =>
      (order.details || []).map((d: any) => {
        // Replace runOrder and allocation if needed
        const runOrderValue = d.runOrder === 99 ? "DIRECT" : d.runOrder;

        let apl = d.allocateProductionLine;
        if (runOrderValue === "DIRECT") {
          apl = "DIRECT";
        } else if (Array.isArray(apl)) {
          apl = apl.join(",");
        }

        const row: Record<string, any> = {
          "MS No": order.MSRef,
          "Order Number": order.OrderNumber1,
          "Item Code": order.ItemCode,
          "Item Name": order.ItemName,
          "Run Order": runOrderValue,
          State: order.StateDelivery,
          Key: order.Key,
          "Production Line": apl ?? "",
        };

        // Continue adding nested headers except allocateProductionLine
        for (const key of getFilteredHeaders(nestedOrderTrackingHeaders)) {
          if (key === "allocateProductionLine") continue; // skip it
          if (key === "runOrder") continue; // skip it
          const value = d[key];
          row[nestedOrderTrackingHeaders[key].name] = factoryDateFields.has(key)
            ? formatDateTime(value)
            : value ?? "";
        }

        return row;
      })
    );

    // Sheet 3: Shipments
    const shipmentHeaders = {
      shipmentNo: { name: t("shipmentNo"), width: 1, textAlign: "left" },
      containerNo: { name: t("containerNo"), width: 1, textAlign: "left" },
      shipmentQty: { name: t("shipmentQty"), width: 1, textAlign: "left" },
      ETD: { name: t("etd"), width: 1, textAlign: "left" },
      duration: { name: t("Duration"), width: 1, textAlign: "left" },
      ETA: { name: t("eta"), width: 1, textAlign: "left" },
    };

    const shipmentSheet = filteredOrders.flatMap((order) =>
      (order.details || []).flatMap((detail: any) =>
        (detail.shipments || []).map((s: any) => {
          // Replace runOrder and allocation if needed
          const runOrderValue =
            detail.runOrder === 99 ? "DIRECT" : detail.runOrder;
          let apl = detail.allocateProductionLine;
          if (runOrderValue === "DIRECT") {
            apl = "DIRECT";
          } else if (Array.isArray(apl)) {
            apl = apl.join(",");
          }

          const row: Record<string, any> = {
            "MS No": order.MSRef,
            "Order Number": order.OrderNumber1,
            "Run Order": runOrderValue,
            "Item Code": order.ItemCode,
            "Item Name": order.ItemName,
            State: order.StateDelivery,
            Key: order.Key,
            "Production Line": apl ?? "",
          };

          for (const key in shipmentHeaders) {
            const value = s[key];
            row[shipmentHeaders[key].name] = shipmentDateFields.has(key)
              ? formatDate(value)
              : value ?? "";
          }

          return row;
        })
      )
    );

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(mainSheet),
      sheetNames[0]
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(factorySheet),
      sheetNames[1]
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.json_to_sheet(shipmentSheet),
      sheetNames[2]
    );

    // --- NATIVE EXPORT (Expo iOS/Android) ---
    if (Platform.OS !== "web") {
      const wbout = XLSX.write(wb, { type: "base64", bookType: "xlsx" });
      const uri = FileSystem.cacheDirectory + `${fileName}.xlsx`;

      await FileSystem.writeAsStringAsync(uri, wbout, {
        encoding: FileSystem.EncodingType.Base64,
      });

      await Sharing.shareAsync(uri, {
        mimeType:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        dialogTitle: "Export Excel",
        UTI: "com.microsoft.excel.xlsx",
      });
    }

    // --- WEB EXPORT ---
    else {
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error("Export error:", error);
  }
};
